import { Button, Modal, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconFile } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { UnifiedDocument } from 'src/suitability-report/hooks/useAllClientDocuments';
import PageLoader from 'src/UI/loader/PageLoader';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { EmailDraftAttachment } from '../chat/types';

// Microsoft Graph rejects inline attachments above ~3 MB; the backend enforces the same cap.
const MAX_ATTACHMENT_BYTES = 3 * 1024 * 1024;

const DOCUMENT_TYPE_LABELS: Record<UnifiedDocument['documentType'], string> = {
  'suitability-report': 'Suitability report',
  'meeting-agenda': 'Meeting agenda',
  'data-export': 'Data export',
  'meeting-note': 'Meeting note',
  'data-upload': 'Uploaded document',
};

type Props = {
  opened: boolean;
  onClose: () => void;
  onSelect: (attachment: EmailDraftAttachment) => void;
  clientId?: string;
  familyId?: string;
};

export function EmailAttachmentPicker({ opened, onClose, onSelect, clientId, familyId }: Props) {
  // Queried directly rather than via useAllClientDocuments: that hook requires a
  // CurrentClientProvider, which the sidebar (rendered on every page) is not always inside.
  const {
    data: documents = [],
    isLoading,
    isError,
  } = useQuery<UnifiedDocument[]>({
    queryKey: familyId
      ? ['families', familyId, 'all-documents']
      : ['clients', clientId, 'all-documents'],
    enabled: opened && !!(familyId || clientId),
    staleTime: 30000,
  });

  const attachableDocuments = documents.filter(document => document.fileId && document.file);

  const handleSelect = (document: UnifiedDocument) => {
    if (!document.fileId || !document.file) return;
    onSelect({ fileId: document.fileId, fileName: document.file.fileName });
    onClose();
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Attach a document" size="md">
      {isLoading && <PageLoader absolute={false} />}
      {isError && (
        <Text size="textSm" c="red.7">
          The documents could not be loaded. Please try again.
        </Text>
      )}
      {!isLoading && !isError && attachableDocuments.length === 0 && (
        <Text size="textSm" c="gray.7">
          There are no documents available to attach.
        </Text>
      )}
      {!isLoading && !isError && attachableDocuments.length > 0 && (
        <Stack gap="xs">
          {attachableDocuments.map(document => {
            const tooLarge = (document.file?.fileSize ?? 0) > MAX_ATTACHMENT_BYTES;
            const row = (
              <UnstyledButton
                key={document.id}
                onClick={() => handleSelect(document)}
                disabled={tooLarge}
                p="xs"
                style={{
                  borderRadius: 'var(--mantine-radius-sm)',
                  border: '1px solid var(--mantine-color-gray-3)',
                  opacity: tooLarge ? 0.5 : undefined,
                  width: '100%',
                }}
              >
                <Stack gap={2}>
                  <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9" truncate>
                    <IconFile
                      size={13}
                      style={{ verticalAlign: 'text-bottom', marginRight: 4 }}
                      aria-hidden
                    />
                    {document.file?.fileName}
                  </Text>
                  <Text size="textXs" c="gray.6">
                    {DOCUMENT_TYPE_LABELS[document.documentType]}
                  </Text>
                </Stack>
              </UnstyledButton>
            );
            return tooLarge ? (
              <Tooltip key={document.id} label="Too large to attach (over 3 MB)">
                {row}
              </Tooltip>
            ) : (
              row
            );
          })}
        </Stack>
      )}
      <Button variant="subtle" color="gray" size="xs" mt="sm" onClick={onClose}>
        Cancel
      </Button>
    </Modal>
  );
}
